// 洗濯表示パネル専用の表示変換・検索結果解釈・Grid 連携をまとめます。
export function createWashPanelService(deps) {
    const {
        fnc,
        isEmpty,
        isEnabled,
        DeepCopy,
        CELLTYPE,
        DicVal,
        GridData,
        SetGridData,
        GridApi,
        entrydataRef,
        entrydetailRef,
        normalizeKakousijiUiString,
        findColumnInfoByCandidates,
        normalizeFukushizaiContext,
        normalizeColorCd,
        readRowValueIgnoreCase,
        readFukushizaiItemGroupFromAnyRow,
        rowMatchesFukushizaiItemGroup,
        WASH_PANEL_SECTION_IDS,
        WASH_STP_SEARCH_CHANGE_URL,
        dispatch,
        setchangeurl,
        changeurl,
        washStpSearchChangeUrlBackupRef,
        washStpSearchRoutingActiveRef,
        washlists,
        ensureDetailRowsByTypeForMutation,
        qualityTargetColorsForContext,
        readQualityDisplayTargetColors,
        nextDetailRowIdForRows,
        stripQualityDisplayMetaFields,
        isInvalidDisplayRow,
        setRowContextForPanel,
        applyGridRowsNow,
        ROW_ID_FIELD,
        WASH_PANEL_TYPE,
        WASH_STP_CODE_FIELD,
        WASH_STP_CODE_DISPLAY_FIELDS,
        WASH_STP_SEARCH_DATAID,
        WASH_STP_SENTAKU_FIELDS,
        isGridDomReady,
        selectedQualityContextRef,
        scheduleGridDataRerender,
        rerender,
        entrylock,
        layoutlock
    } = deps;

    // 洗濯マーク候補一覧を index から取得します。
    function getWashMarkListByIndex(markIndex) {
        const index = Number(markIndex) - 1;
        return Array.isArray(washlists?.[index]) ? washlists[index] : [];
    }

    // 洗濯マークの保存値を表示値へ解決します。
    function resolveWashMarkValueForRenderer(rawValue, markIndex) {
        const normalizedValue = normalizeKakousijiUiString(rawValue);
        if (normalizedValue === "") return { value: "", matched: null, matchedBy: "" };
        const list = getWashMarkListByIndex(markIndex);
        const candidates = ["value", "key", "code"];
        for (let i = 0; i < candidates.length; i++) {
            const fieldId = candidates[i];
            const hit = list.find((item) =>
                normalizeKakousijiUiString(DicVal(item, fieldId, "")) === normalizedValue
            );
            if (!isEmpty(hit)) {
                const value = normalizeKakousijiUiString(DicVal(hit, "value", ""));
                return { value: value === "" ? normalizedValue : value, matched: hit, matchedBy: fieldId };
            }
        }
        return { value: normalizedValue, matched: null, matchedBy: "" };
    }

    // 洗濯表示の保存対象となる物理項目があるかを判定します。
    function hasWashPhysicalValues(row) {
        return !isEmpty(row) && Object.keys(row || {}).some((key) => {
            if (key === "rowid") return false;
            if (/^mark[1-7]$/i.test(String(key ?? ""))) return false;
            if (/^STP_SENTAKU[1-7]$/i.test(String(key ?? ""))) return false;
            const value = String(DicVal(row, key, "") ?? "").trim();
            return value !== "";
        });
    }

    // 洗濯表示の画面専用項目を除いた保存用 row を返します。
    function stripWashDisplayOnlyFields(row) {
        const out = {};
        Object.keys(row || {}).forEach((key) => {
            if (key === "rowid") return;
            if (/^mark[1-7]$/i.test(String(key ?? ""))) return;
            if (/^STP_SENTAKU[1-7]$/i.test(String(key ?? ""))) return;
            out[key] = row[key];
        });
        return out;
    }

    // 洗濯表示の保存行を D_SSDE 保存用へ変換します。
    //
    // サーバの保存は伝票単位の「全削除→送信行の再INSERT」のため、表示中の行だけを返すと
    // 非表示コンテキスト(別アイテム/別グループ/代表色以外)の既存行が保存のたびに消える。
    // 119-123 の toQualitySaveRows と同じく、現コンテキスト以外の既存 detail 行を温存し、
    // 表示行は対象色へ展開して現コンテキスト分を再生成する(applyWashStpRows と同じ規則)。
    function toWashSaveRows(rows, contextOverride = null) {
        const displayRows = Array.isArray(rows) ? rows : [];
        const context = normalizeFukushizaiContext(contextOverride || selectedQualityContextRef.current);
        const detail = ensureDetailRowsByTypeForMutation(WASH_PANEL_TYPE);
        // 論理削除済み(INVALID=1)の行は保存 payload へ載せない(送らない=物理削除)。
        // 送ると INVALID 列が保存可能列セットから strip され、サーバの既存行マージで
        // INVALID=0 に戻って削除したグループが復活する。早期 return の経路も含めて
        // 取得時点で除外しておく。
        const existingRows = (Array.isArray(detail.rows) ? detail.rows : [])
            .filter((row) => !(typeof isInvalidDisplayRow === "function" && isInvalidDisplayRow(row, WASH_PANEL_TYPE)));

        // 表示行から保存対象の物理行を抽出します。
        // 空表示のプレースホルダ行はコンテキスト列を持つため、物理値判定より先に除外します。
        const savePhysicalRows = [];
        displayRows.forEach((row) => {
            if (isEmpty(row)) return;
            if (isEnabled(DicVal(row, "__displayOnly", false))) return;
            if (isEnabled(DicVal(row, "__QUALITY_EMPTY_INPUT_ROW", false))) return;
            const physicalRow = stripWashDisplayOnlyFields(stripQualityDisplayMetaFields(row));
            delete physicalRow.__displayOnly;
            if (!hasWashPhysicalValues(physicalRow)) return;
            savePhysicalRows.push(physicalRow);
        });

        // コンテキスト未確定時は既存 detail を維持します(非表示行を落とさない)。
        if (context.itemCd === "" || context.groupNo === "") {
            return existingRows.length === 0 ? null : existingRows;
        }

        // 現コンテキスト以外の既存行を温存します。
        const preservedRows = existingRows.filter((row) => {
            const rowContext = readFukushizaiItemGroupFromAnyRow(row);
            if (rowContext.itemCd === "" || rowContext.groupNo === "" || rowContext.colorCd === "") return false;
            return !rowMatchesFukushizaiItemGroup(row, context.itemCd, context.groupNo);
        });

        // 表示行が無い場合は既存 detail を維持します(全行削除の確定はここでは行わない)。
        if (savePhysicalRows.length === 0) {
            return existingRows.length === 0 ? null : existingRows;
        }

        const metaTargetColors = displayRows.flatMap((row) => readQualityDisplayTargetColors(row));
        const targetColors = [...new Set([
            ...qualityTargetColorsForContext(context, existingRows, WASH_PANEL_TYPE),
            ...metaTargetColors,
            context.colorCd
        ].map((value) => normalizeColorCd(value)).filter((value) => value !== ""))];
        if (targetColors.length === 0) {
            return existingRows.length === 0 ? null : existingRows;
        }

        let nextRowId = nextDetailRowIdForRows(preservedRows);
        const outRows = [...preservedRows];
        savePhysicalRows.forEach((physicalRow) => {
            targetColors.forEach((colorCd) => {
                const next = { ...physicalRow, [ROW_ID_FIELD]: String(nextRowId++) };
                setRowContextForPanel(next, WASH_PANEL_TYPE, context.itemCd, context.groupNo, colorCd);
                outRows.push(next);
            });
        });

        entrydetailRef.current[detail.key] = outRows;
        return outRows;
    }

    // WashGrid 用表示の mark 値を正規化します。
    function normalizeWashMarkValueForRenderer(rawValue, markIndex) {
        return resolveWashMarkValueForRenderer(rawValue, markIndex).value;
    }

    // 洗濯表示の debug trace は現状無効化したままにします。
    function debugWashMarkMapping(panelType, row, beforeMarks, afterMarks, matches) {
        return;
    }

    // D_SSDE 行を WashGrid 表示用の mark1 〜 mark7 に展開します。
    function applyWashMarkDisplayFields(row) {
        if (isEmpty(row) || typeof row !== "object") return row;
        const next = { ...row };
        const beforeMarks = {};
        const afterMarks = {};
        const matches = {};
        for (let i = 1; i <= 7; i++) {
            const markKey = `mark${i}`;
            const stpKey = `STP_SENTAKU${i}`;
            const current = normalizeKakousijiUiString(DicVal(next, markKey, ""));
            const stpValue = normalizeKakousijiUiString(readRowValueIgnoreCase(next, stpKey));
            const rawValue = current !== "" ? current : stpValue;
            beforeMarks[markKey] = rawValue;
            const resolved = resolveWashMarkValueForRenderer(rawValue, i);
            if (rawValue !== "") next[markKey] = resolved.value;
            afterMarks[markKey] = normalizeKakousijiUiString(DicVal(next, markKey, ""));
            matches[markKey] = {
                washlistIndex: i,
                matchedBy: resolved.matchedBy,
                key: DicVal(resolved.matched, "key", ""),
                code: DicVal(resolved.matched, "code", ""),
                value: DicVal(resolved.matched, "value", "")
            };
        }
        debugWashMarkMapping(WASH_PANEL_TYPE, row, beforeMarks, afterMarks, matches);
        return next;
    }

    function isWashStpCodeKey(key) {
        const value = normalizeKakousijiUiString(key).toUpperCase();
        return value === WASH_STP_CODE_FIELD || value === "STP_CODE" || value === "CODE";
    }

    function readWashStpKeyValueCode(row) {
        if (isEmpty(row) || typeof row !== "object") return "";
        const key = readRowValueIgnoreCase(row, "key");
        if (!isWashStpCodeKey(key)) return "";
        return normalizeKakousijiUiString(readRowValueIgnoreCase(row, "value"));
    }

    function pickFirstWashStpValue(values) {
        if (!Array.isArray(values)) return "";
        for (let i = 0; i < values.length; i++) {
            const value = normalizeKakousijiUiString(values[i]);
            if (value !== "") return value;
        }
        return "";
    }

    function hasWashRowField(row, fieldId) {
        if (isEmpty(row) || isEmpty(fieldId)) return false;
        const target = String(fieldId ?? "").trim().toUpperCase();
        return Object.keys(row || {}).some((key) => String(key ?? "").trim().toUpperCase() === target);
    }

    function pushWashStpSearchCandidate(candidates, value) {
        if (isEmpty(value)) return;
        if (Array.isArray(value)) {
            value.forEach((item) => pushWashStpSearchCandidate(candidates, item));
            return;
        }
        if (typeof value !== "object") return;
        const data = DicVal(value, "data", null);
        if (!isEmpty(data)) candidates.push(data);
        candidates.push(value);
    }

    function hasWashStpSearchResultFields(row) {
        if (isEmpty(row) || typeof row !== "object") return false;
        const stpCode = normalizeKakousijiUiString(readRowValueIgnoreCase(row, "STP_CODE"));
        const code = normalizeKakousijiUiString(readRowValueIgnoreCase(row, "code"));
        if (stpCode !== "" || code !== "") return true;
        return WASH_STP_SENTAKU_FIELDS.some((fieldId) =>
            normalizeKakousijiUiString(readRowValueIgnoreCase(row, fieldId)) !== ""
        );
    }

    function readWashStpTopLevelValue(result, datainfo) {
        if (isEmpty(result) || typeof result !== "object") return "";
        const resultKey = readRowValueIgnoreCase(result, "key");
        const resultValue = readRowValueIgnoreCase(result, "value");
        const columnId = readRowValueIgnoreCase(datainfo, "columnid");
        const dbcolumn = readRowValueIgnoreCase(datainfo, "dbcolumn");
        if (isWashStpCodeKey(resultKey) || isWashStpCodeKey(columnId) || isWashStpCodeKey(dbcolumn)) {
            return normalizeKakousijiUiString(resultValue);
        }
        return "";
    }

    function readWashStpSearchResultData(result) {
        const candidates = [];
        pushWashStpSearchCandidate(candidates, DicVal(result, "items", []));
        pushWashStpSearchCandidate(candidates, DicVal(result, "selectedRows", []));
        const rowdata = DicVal(result, "rowdata", null);
        if (hasWashStpSearchResultFields(rowdata)) candidates.push(rowdata);
        const data = DicVal(result, "data", null);
        if (hasWashStpSearchResultFields(data)) candidates.push(data);
        if (hasWashStpSearchResultFields(result)) candidates.push(result);
        const row = candidates.find((candidate) => hasWashStpSearchResultFields(candidate));
        return isEmpty(row) ? {} : row;
    }

    // rowid は M_STP コードの fallback には使いません。
    function readWashStpCodeFromSearchResult(result, datainfo) {
        const item = readWashStpSearchResultData(result);
        const dbcolumn = normalizeKakousijiUiString(DicVal(datainfo, "dbcolumn", "STP_CODE"));
        const dbcolumnValue = dbcolumn.toUpperCase() === "STP_CODE"
            ? readRowValueIgnoreCase(item, dbcolumn)
            : "";
        const itemRows = (Array.isArray(DicVal(result, "items", [])) ? DicVal(result, "items", []) : [])
            .flatMap((candidate) => [candidate, DicVal(candidate, "data", null)]);
        const itemCodeValue = pickFirstWashStpValue(itemRows.flatMap((candidate) => [
            readRowValueIgnoreCase(candidate, "STP_CODE"),
            readRowValueIgnoreCase(candidate, "code")
        ]));
        const itemKeyValue = pickFirstWashStpValue((Array.isArray(DicVal(result, "items", [])) ? DicVal(result, "items", []) : [])
            .flatMap((candidate) => [
                readWashStpKeyValueCode(candidate),
                readWashStpKeyValueCode(DicVal(candidate, "data", null))
            ]));
        return pickFirstWashStpValue([
            readRowValueIgnoreCase(item, "STP_CODE"),
            readRowValueIgnoreCase(item, "code"),
            itemCodeValue,
            itemKeyValue,
            readWashStpTopLevelValue(result, datainfo),
            readRowValueIgnoreCase(item, WASH_STP_CODE_FIELD),
            dbcolumnValue
        ]);
    }

    function readWashStpNameFromSearchResult(result, datainfo, code) {
        const item = readWashStpSearchResultData(result);
        const dbcolumnnm = normalizeKakousijiUiString(DicVal(datainfo, "dbcolumnnm", ""));
        const name = pickFirstWashStpValue([
            dbcolumnnm === "" ? "" : DicVal(item, dbcolumnnm, ""),
            DicVal(item, "STP_NAME", ""),
            DicVal(item, "STP_NM", ""),
            DicVal(item, "name", ""),
            DicVal(item, "label", "")
        ]);
        return name === "" ? code : name;
    }

    // 洗濯表示の検索列定義を組み立てます。
    function findWashStpColumnInfo() {
        return findColumnInfoByCandidates([WASH_STP_CODE_FIELD], [WASH_PANEL_TYPE, ...WASH_PANEL_SECTION_IDS])
            || findColumnInfoByCandidates([WASH_STP_CODE_FIELD], []);
    }

    // 共通検索の changeurl を指定 route へ差し替えます。
    function createSearchChangeUrlWithCommonMasterRoute(baseChangeUrl, route) {
        const source = Array.isArray(baseChangeUrl) ? [...baseChangeUrl] : [];
        const filtered = source.filter((value) =>
            typeof value !== "string" || value.toLowerCase().indexOf("common/getsearchmasterlist=") !== 0
        );
        filtered.push(route);
        return filtered;
    }

    // 洗濯パターン検索用の changeurl を生成します。
    function createWashStpSearchChangeUrl(baseChangeUrl) {
        return createSearchChangeUrlWithCommonMasterRoute(baseChangeUrl, WASH_STP_SEARCH_CHANGE_URL);
    }

    // 洗濯パターン検索用の datainfo を構築します。
    function buildWashStpSearchDataInfo() {
        const source = findWashStpColumnInfo();
        const base = isEmpty(source) ? {} : DeepCopy(source);
        const datatype = String(DicVal(base, "datatype", "") ?? "").trim();
        return {
            ...base,
            columnid: WASH_STP_CODE_FIELD,
            columnnm: DicVal(base, "columnnm", "洗濯パターンコード"),
            datatype: datatype === "" ? CELLTYPE.MASTER : base.datatype,
            dataid: WASH_STP_SEARCH_DATAID,
            dataidctrl: WASH_STP_SEARCH_DATAID,
            mastertype: WASH_STP_SEARCH_DATAID,
            dbcolumn: DicVal(base, "dbcolumn", "STP_CODE") || "STP_CODE",
            dbcolumnnm: DicVal(base, "dbcolumnnm", DicVal(base, "dbcolumn", "STP_CODE")) || "STP_CODE",
            multiple: false,
            primary: false,
            trigger: Array.isArray(base.trigger) && base.trigger.length !== 0
                ? base.trigger
                : WASH_STP_SENTAKU_FIELDS.map((fieldId) => ({ columnid: fieldId, dbcolumn: fieldId }))
        };
    }

    // 洗濯パターン検索用に changeurl を切り替えます。
    function routeWashStpSearchChangeUrl() {
        washStpSearchChangeUrlBackupRef.current = Array.isArray(changeurl) ? [...changeurl] : [];
        dispatch(setchangeurl(createWashStpSearchChangeUrl(washStpSearchChangeUrlBackupRef.current)));
        washStpSearchRoutingActiveRef.current = true;
    }

    // 洗濯パターン検索用の changeurl を元へ戻します。
    function restoreWashStpSearchChangeUrl() {
        if (!washStpSearchRoutingActiveRef.current) return;
        dispatch(setchangeurl(washStpSearchChangeUrlBackupRef.current));
        washStpSearchRoutingActiveRef.current = false;
    }

    // WashGrid の初期 1 行を返します。
    function defaultWashRows() {
        return [{ rowid: "0", mark1: "", mark2: "", mark3: "", mark4: "", mark5: "", mark6: "", mark7: "", __displayOnly: true }];
    }

    // M_STP 選択結果から WashGrid 表示 1 行を組み立てます。
    function buildWashRowsWithStpMaster(panelInfo, code, masterRow = {}, displayName = "") {
        const rows = GridData(panelInfo);
        const baseRows = Array.isArray(rows) && rows.length !== 0 ? rows : defaultWashRows();
        return baseRows.map((row, rowIndex) => {
            if (rowIndex !== 0) return row;
            const next = { ...row };
            delete next.__displayOnly;
            // パターンを反映した時点で実データ行になるため、空表示プレースホルダの印は外す。
            delete next.__QUALITY_EMPTY_INPUT_ROW;
            next[ROW_ID_FIELD] = "0";
            next[WASH_STP_CODE_FIELD] = code;
            // 選択したマスタのパターン区分を行へ引き継ぐ。
            // (プレースホルダ既定値の区分が残ると、保存データと次回検索の絞り込みが選択内容と食い違う)
            const masterPatankbn = normalizeKakousijiUiString(readRowValueIgnoreCase(masterRow, "STP_PATANKBN"));
            if (masterPatankbn !== "") next.SSDE_PATANKBN = masterPatankbn;
            WASH_STP_CODE_DISPLAY_FIELDS.forEach((fieldId) => {
                next[fieldId] = fieldId.endsWith("_nm") ? (displayName || code) : code;
            });
            WASH_STP_SENTAKU_FIELDS.forEach((fieldId, index) => {
                const markField = `mark${index + 1}`;
                const value = pickFirstWashStpValue([
                    readRowValueIgnoreCase(masterRow, markField),
                    readRowValueIgnoreCase(masterRow, fieldId)
                ]);
                next[fieldId] = value;
                next[markField] = value;
            });
            return next;
        });
    }

    // 洗濯パターン選択結果から反映用データを生成します。
    function applyWashStpMasterSelection(panelInfo, result, datainfo, washGridPayload = null) {
        const code = normalizeKakousijiUiString(readWashStpCodeFromSearchResult(result, datainfo));
        if (code === "") {
            return;
        }
        const displayName = readWashStpNameFromSearchResult(result, datainfo, code);
        const selectedData = readWashStpSearchResultData(result);
        const applyRows = (masterRow) => {
            const mergedMaster = isEmpty(masterRow) ? selectedData : { ...selectedData, ...masterRow };
            const rowsForApply = buildWashRowsWithStpMaster(panelInfo, code, mergedMaster, displayName);
            applyWashStpRows(panelInfo, rowsForApply, washGridPayload);
        };
        const hasSentaku = WASH_STP_SENTAKU_FIELDS.some((fieldId) =>
            normalizeKakousijiUiString(readRowValueIgnoreCase(selectedData, fieldId)) !== ""
        );
        const dataid = normalizeKakousijiUiString(
            DicVal(datainfo, "dataid", DicVal(datainfo, "dataidctrl", DicVal(datainfo, "mastertype", "M_STP")))
        ) || "M_STP";
        const masterDataid = dataid === WASH_STP_SEARCH_DATAID ? "M_STP" : dataid;
        if (hasSentaku || isEmpty(fnc.current) || typeof fnc.current.getMasterdata !== "function") {
            applyRows(selectedData);
            return;
        }
        const trigger = WASH_STP_SENTAKU_FIELDS.map((fieldId) => ({ columnid: fieldId, dbcolumn: fieldId }));
        fnc.current.getMasterdata(masterDataid, code, DicVal(datainfo, "keyinfo", ""), trigger, null, (masterRow) => {
            applyRows(masterRow);
        });
    }

    // 洗濯表示用の M_STP 検索を開きます。
    function openWashStpMasterSearch(panelInfo, washGridPayload = null) {
        if (entrylock || layoutlock) {
            return;
        }
        const datainfo = buildWashStpSearchDataInfo();
        const firstGridRow = GridData(panelInfo)[0] || {};
        const searchRowData = {
            ...(entrydataRef.current || {}),
            ...firstGridRow
        };
        // 空表示のプレースホルダ行は表示順既定値としてパターン区分("1")を持つため、
        // そのまま検索条件へ渡すと M_STP がパターン区分1で絞り込まれてしまう
        // (区分1のマスタが全て無効な環境では候補が0件になる)。
        // 絵表示コード(SSDE_STPCD)が未設定の行も同様(摘要のみを保存した「絵表示なし行」は
        // 実データ行だがパターン区分=1を持つため、絞り込むと候補が0件になる)。
        // 絵表示コードが選択済みの場合だけパターン区分で絞り込む。
        const firstRowStpCode = normalizeKakousijiUiString(readRowValueIgnoreCase(firstGridRow, WASH_STP_CODE_FIELD));
        if (isEnabled(DicVal(firstGridRow, "__QUALITY_EMPTY_INPUT_ROW", false)) || firstRowStpCode === "") {
            delete searchRowData.SSDE_PATANKBN;
        }
        routeWashStpSearchChangeUrl();
        fnc.current.searchlistopen({
            label: DicVal(datainfo, "columnnm", "洗濯パターンコード"),
            multiple: false,
            rowmode: false,
            data: datainfo,
            rowdata: searchRowData,
            doApply: (result) => {
                restoreWashStpSearchChangeUrl();
                fnc.current.searchlistclose();
                applyWashStpMasterSelection(panelInfo, result, datainfo, washGridPayload);
            }
        });
    }

    // 洗濯表示明細を WashGrid 表示用に正規化します。
    function normalizeWashRowsForDisplay(rows) {
        const list = Array.isArray(rows) ? rows : [];
        if (list.length === 0) return defaultWashRows();
        const normalized = list.map((row, index) => {
            const nextRow = { ...row };
            nextRow.rowid = String(DicVal(row, "rowid", index) ?? index);
            const beforeMarks = {};
            const afterMarks = {};
            const matches = {};
            for (let i = 1; i <= 7; i++) {
                const markKey = `mark${i}`;
                const stpKey = `STP_SENTAKU${i}`;
                const current = normalizeKakousijiUiString(DicVal(row, markKey, ""));
                const stpValue = normalizeKakousijiUiString(readRowValueIgnoreCase(row, stpKey));
                const rawValue = current !== "" ? current : stpValue;
                const resolved = resolveWashMarkValueForRenderer(rawValue, i);
                beforeMarks[markKey] = rawValue;
                nextRow[markKey] = resolved.value;
                afterMarks[markKey] = resolved.value;
                matches[markKey] = {
                    washlistIndex: i,
                    matchedBy: resolved.matchedBy,
                    key: DicVal(resolved.matched, "key", ""),
                    code: DicVal(resolved.matched, "code", ""),
                    value: DicVal(resolved.matched, "value", "")
                };
            }
            debugWashMarkMapping(WASH_PANEL_TYPE, row, beforeMarks, afterMarks, matches);
            return nextRow;
        });
        return normalized.length === 0 ? defaultWashRows() : normalized;
    }

    // WashGrid の doApply 互換 payload を生成します。
    function buildWashApplyPayload(row) {
        const source = isEmpty(row) ? {} : row;
        return {
            mark1: readRowValueIgnoreCase(source, "mark1"),
            mark2: readRowValueIgnoreCase(source, "mark2"),
            mark3: readRowValueIgnoreCase(source, "mark3"),
            mark4: readRowValueIgnoreCase(source, "mark4"),
            mark5: readRowValueIgnoreCase(source, "mark5"),
            mark6: readRowValueIgnoreCase(source, "mark6"),
            mark7: readRowValueIgnoreCase(source, "mark7")
        };
    }

    // WashGrid の 1 行へ反映する表示用データを組み立てます。
    function buildWashGridRowData(row, currentData = {}) {
        const source = isEmpty(row) ? {} : row;
        const next = { ...(currentData || {}), ...source };
        next[ROW_ID_FIELD] = normalizeKakousijiUiString(DicVal(next, ROW_ID_FIELD, "0")) || "0";
        const code = normalizeKakousijiUiString(readRowValueIgnoreCase(source, WASH_STP_CODE_FIELD));
        if (code !== "") next[WASH_STP_CODE_FIELD] = code;
        WASH_STP_CODE_DISPLAY_FIELDS.forEach((fieldId) => {
            const value = normalizeKakousijiUiString(readRowValueIgnoreCase(source, fieldId));
            if (value !== "") next[fieldId] = value;
        });
        WASH_STP_SENTAKU_FIELDS.forEach((fieldId, index) => {
            const markField = `mark${index + 1}`;
            const value = hasWashRowField(source, fieldId)
                ? normalizeKakousijiUiString(readRowValueIgnoreCase(source, fieldId))
                : hasWashRowField(source, markField)
                    ? normalizeKakousijiUiString(readRowValueIgnoreCase(source, markField))
                    : pickFirstWashStpValue([
                        readRowValueIgnoreCase(next, fieldId),
                        readRowValueIgnoreCase(next, markField)
                    ]);
            next[fieldId] = value;
            next[markField] = value;
        });
        if (normalizeKakousijiUiString(readRowValueIgnoreCase(next, WASH_STP_CODE_FIELD)) !== "") {
            delete next.__displayOnly;
        }
        return next;
    }

    // D_SSDE 系の洗濯表示明細かどうかを判定します。
    function isWashSsdeDetailRow(row) {
        if (isEmpty(row) || typeof row !== "object") return false;
        return (
            Object.prototype.hasOwnProperty.call(row, WASH_STP_CODE_FIELD) ||
            Object.prototype.hasOwnProperty.call(row, "SSDE_PATANKBN") ||
            Object.prototype.hasOwnProperty.call(row, "SSDE_NO")
        );
    }

    // WashGrid で反映対象にする行ノードを解決します。
    function resolveWashGridTargetRowNode(gridApi, washGridPayload) {
        if (isEmpty(gridApi)) return null;
        const payloadNode = DicVal(washGridPayload, "rowNode", null);
        if (!isEmpty(payloadNode)) return payloadNode;
        if (typeof gridApi.getSelectedNodes === "function") {
            const selectedNodes = gridApi.getSelectedNodes();
            if (Array.isArray(selectedNodes) && selectedNodes.length !== 0) return selectedNodes[0];
        }
        if (typeof gridApi.getFocusedCell === "function" && typeof gridApi.getDisplayedRowAtIndex === "function") {
            const focused = gridApi.getFocusedCell();
            const rowIndex = DicVal(focused, "rowIndex", null);
            if (rowIndex !== null && rowIndex !== undefined) {
                const focusedNode = gridApi.getDisplayedRowAtIndex(rowIndex);
                if (!isEmpty(focusedNode)) return focusedNode;
            }
        }
        if (typeof gridApi.getRowNode === "function") {
            const rowNode = gridApi.getRowNode("0");
            if (!isEmpty(rowNode)) return rowNode;
        }
        let firstNode = null;
        if (typeof gridApi.forEachNode === "function") {
            gridApi.forEachNode((node) => {
                if (isEmpty(firstNode)) firstNode = node;
            });
        }
        return firstNode;
    }

    // 洗濯表示(124)を紐づけるコンテキストを補完します。
    // 洗濯表示は品質表示ドメインのため、品質側の選択コンテキストを使う。
    function resolveWashApplyContext(firstRow, existingRows = []) {
        const selected = normalizeFukushizaiContext(selectedQualityContextRef.current);
        const rowContext = readFukushizaiItemGroupFromAnyRow(firstRow || {});
        const context = {
            itemCd: selected.itemCd || rowContext.itemCd,
            groupNo: selected.groupNo || rowContext.groupNo,
            colorCd: selected.colorCd || rowContext.colorCd
        };
        // 現在選択中の文脈だけで不足する場合は、既存明細から同系統の文脈を拾います。
        if (context.itemCd === "" || context.groupNo === "") {
            const fallback = (Array.isArray(existingRows) ? existingRows : []).find((row) => {
                const existingContext = readFukushizaiItemGroupFromAnyRow(row);
                if (existingContext.itemCd === "" || existingContext.groupNo === "") return false;
                if (context.itemCd !== "" && existingContext.itemCd !== context.itemCd) return false;
                if (context.groupNo !== "" && existingContext.groupNo !== context.groupNo) return false;
                return true;
            });
            if (!isEmpty(fallback)) {
                const fallbackContext = readFukushizaiItemGroupFromAnyRow(fallback);
                context.itemCd = context.itemCd || fallbackContext.itemCd;
                context.groupNo = context.groupNo || fallbackContext.groupNo;
                context.colorCd = context.colorCd || fallbackContext.colorCd;
            }
        }
        // 色だけ未確定の場合は、既存行から同一 item/group の色を引き継ぎます。
        if (context.itemCd !== "" && context.groupNo !== "" && context.colorCd === "") {
            const matched = (Array.isArray(existingRows) ? existingRows : []).find((row) =>
                rowMatchesFukushizaiItemGroup(row, context.itemCd, context.groupNo)
            );
            if (!isEmpty(matched)) context.colorCd = readFukushizaiItemGroupFromAnyRow(matched).colorCd;
        }
        return normalizeFukushizaiContext(context);
    }

    // WashGrid 側の検索反映経路へ mark 値を戻します。
    function applyWashGridPayload(washGridPayload, row) {
        if (isEmpty(washGridPayload) || isEmpty(row)) {
            return false;
        }
        const payload = buildWashApplyPayload(row);
        const gridApi = DicVal(washGridPayload, "gridApi", null);
        // Grid API があれば、選択中またはフォーカス中の行へ直接反映します。
        if (!isEmpty(gridApi)) {
            const rowNode = resolveWashGridTargetRowNode(gridApi, washGridPayload);
            if (!isEmpty(rowNode)) {
                const nextRowData = buildWashGridRowData(row, rowNode.data || {});
                Object.keys(nextRowData).forEach((fieldId) => {
                    if (typeof rowNode.setDataValue === "function") {
                        rowNode.setDataValue(fieldId, DicVal(nextRowData, fieldId, ""));
                    }
                });
                if (typeof rowNode.setData === "function") rowNode.setData(nextRowData);
                if (typeof gridApi.refreshCells === "function") {
                    gridApi.refreshCells({ rowNodes: [rowNode], force: true });
                }
                if (typeof gridApi.redrawRows === "function") {
                    gridApi.redrawRows({ rowNodes: [rowNode] });
                }
                return true;
            }
        }
        // Grid API が取れない場合は、従来の doApply コールバックへフォールバックします。
        const doApply = DicVal(washGridPayload, "doApply", null);
        if (typeof doApply === "function") {
            doApply(payload);
            return true;
        }
        return false;
    }

    // M_STP 選択結果を親 GridData・D_SSDE 明細・WashGrid 表示へ同期します。
    function applyWashStpRows(panelInfo, rows, washGridPayload = null) {
        const nextRows = Array.isArray(rows) ? rows : [];
        const firstRow = nextRows.length !== 0 ? nextRows[0] : null;
        const detail = ensureDetailRowsByTypeForMutation(WASH_PANEL_TYPE);
        const existingRows = Array.isArray(entrydetailRef.current[detail.key]) ? entrydetailRef.current[detail.key] : [];
        const context = resolveWashApplyContext(firstRow, existingRows);
        // 副資材コンテキストが取れる場合だけ、色展開を含めて D_SSDE 明細へ再構成します。
        if (Array.isArray(nextRows) && nextRows.length !== 0 && context.itemCd !== "" && context.groupNo !== "") {
            const metaTargetColors = nextRows.flatMap((row) => readQualityDisplayTargetColors(row));
            const rowTargetColors = nextRows.map((row) => readFukushizaiItemGroupFromAnyRow(row).colorCd);
            const targetColors = [...new Set([
                ...qualityTargetColorsForContext(context, existingRows, WASH_PANEL_TYPE),
                ...metaTargetColors,
                ...rowTargetColors,
                context.colorCd
            ].map((value) => normalizeColorCd(value)).filter((value) => value !== ""))];
            // 反映対象色が 1 件以上あるときだけ、既存の同系統行を差し替えます。
            if (targetColors.length !== 0) {
                const preservedRows = existingRows.filter((row) =>
                    !isWashSsdeDetailRow(row) || !rowMatchesFukushizaiItemGroup(row, context.itemCd, context.groupNo)
                );
                let nextRowId = nextDetailRowIdForRows(preservedRows);
                const detailRows = [...preservedRows];
                nextRows.forEach((row) => {
                    const displayRow = stripQualityDisplayMetaFields(row);
                    delete displayRow.__displayOnly;
                    targetColors.forEach((colorCd) => {
                        const next = { ...displayRow, [ROW_ID_FIELD]: String(nextRowId++) };
                        setRowContextForPanel(next, WASH_PANEL_TYPE, context.itemCd, context.groupNo, colorCd);
                        detailRows.push(next);
                    });
                });
                entrydetailRef.current[detail.key] = detailRows;
            }
        }
        SetGridData(panelInfo, nextRows);
        const api = GridApi(panelInfo);
        if (applyWashGridPayload(washGridPayload, firstRow)) {
            // WashGrid 側で直接反映できた場合でも、親側の detail 同期結果はこのまま維持します。
        } else if (!isEmpty(api) && isGridDomReady(panelInfo)) {
            // 親 Grid が描画済みなら、親 GridData から表示行を更新します。
            const rowNode = resolveWashGridTargetRowNode(api, null);
            if (!isEmpty(rowNode) && !isEmpty(firstRow)) {
                const nextRowData = buildWashGridRowData(firstRow, rowNode.data || {});
                Object.keys(nextRowData).forEach((fieldId) => {
                    if (typeof rowNode.setDataValue === "function") {
                        rowNode.setDataValue(fieldId, DicVal(nextRowData, fieldId, ""));
                    }
                });
                if (typeof rowNode.setData === "function") rowNode.setData(nextRowData);
                if (typeof api.refreshCells === "function") {
                    api.refreshCells({ rowNodes: [rowNode], force: true });
                }
                if (typeof api.redrawRows === "function") {
                    api.redrawRows({ rowNodes: [rowNode] });
                }
            } else {
                // 行ノードを特定できない場合は、グリッド全体を安全側で再描画します。
                applyGridRowsNow(panelInfo, nextRows);
            }
        }
        if (typeof scheduleGridDataRerender === "function") {
            scheduleGridDataRerender(panelInfo);
        }
        if (!isEmpty(fnc.current) && typeof fnc.current.modify === "function") {
            fnc.current.modify(true);
        }
        rerender();
    }

    // WashGrid へ渡す関数参照を組み立てます。
    function buildWashGridFunctionRef(panelInfo) {
        const base = fnc.current || {};
        return {
            current: {
                ...base,
                washlistopen: (payload) => {
                    return openWashStpMasterSearch(panelInfo, payload);
                }
            }
        };
    }

    return {
        toWashSaveRows,
        normalizeWashMarkValueForRenderer,
        applyWashMarkDisplayFields,
        normalizeWashRowsForDisplay,
        readWashStpKeyValueCode,
        readWashStpTopLevelValue,
        readWashStpSearchResultData,
        readWashStpCodeFromSearchResult,
        readWashStpNameFromSearchResult,
        defaultWashRows,
        buildWashRowsWithStpMaster,
        applyWashStpRows,
        applyWashStpMasterSelection,
        openWashStpMasterSearch,
        restoreWashStpSearchChangeUrl,
        buildWashGridFunctionRef
    };
}
